import { dateLocale } from "@/lib/dateTime";
import { useCallback, useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { fmtMonth } from "@/components/reports/ReportWidgets";
import type { Task } from "@/types/task";
import type { ProposalListRow } from "@/types/proposal";
import type { SalesHeadSummary, SuperAdminSummary } from "./types";

export interface Bar {
  label: string;
  /** Stacked from the bottom; the first segment is the strong one */
  segments: Array<{ value: number; tone: "gold" | "primary" | "soft" | "danger" }>;
  current?: boolean;
}

export interface Leader {
  id: string;
  name: string;
  won: number;
  deals: number;
  target: number;
}

export interface AttentionRow {
  key: string;
  label: string;
  detail?: string;
  count?: number;
  to: string;
  tone: "danger" | "warning" | "info";
}

export interface QuarterForecast {
  months: Array<{ period: string; label: string; current: boolean; won: number; committed: number; best_case: number; target: number }>;
  quarter: { won: number; committed: number; best_case: number; target: number };
}

export interface FunnelData {
  days: number;
  steps: Array<{ key: string; label: string; count: number; rate: number | null }>;
}

export interface ManagerData {
  role: "SUPER_ADMIN" | "SALES_HEAD";
  kpis: {
    revenue: number;
    revenuePrev: number;
    target: number;
    targetPct: number | null;
    pipeline: number;
    weighted: number;
    winRate: number;
    winRatePrev: number;
    conversion: number;
    conversionPrev: number;
  };
  chart: { title: string; subtitle: string; bars: Bar[] };
  stages: Array<{ id: string; name: string; deals: number; amount: number }>;
  leaders: Leader[];
  leadersTitle: string;
  attention: AttentionRow[];
  approvals: SalesHeadSummary["approval_queue"];
  quotesAwaiting: number | null;
  hot: ProposalListRow[];
  agenda: Task[];
  forecast: QuarterForecast | null;
  funnel: FunnelData | null;
}

const monthRange = () => {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { daysLeft: Math.max(0, last.getDate() - now.getDate()), monthName: now.toLocaleDateString(dateLocale(), { month: "long" }) };
};
export { monthRange };

function fromSuperAdmin(d: SuperAdminSummary): Omit<ManagerData, "hot" | "agenda" | "forecast" | "funnel"> {
  const trend = (d.revenue_trend || []).slice(-12);
  const heads = d.head_leaderboard || [];
  const leaders = (heads.length ? heads : d.top_execs || [])
    .map((r) => ({ id: r.user_id, name: r.name, won: r.won_amount, deals: r.deals_won, target: r.target_amount }))
    .sort((a, b) => b.won - a.won)
    .slice(0, 5);
  const a = d.attention;
  return {
    role: "SUPER_ADMIN",
    kpis: {
      revenue: d.kpis.revenue,
      revenuePrev: d.kpis.revenue_prev,
      target: d.kpis.target_amount,
      targetPct: d.kpis.target_achieved_pct,
      pipeline: d.kpis.pipeline_amount,
      weighted: d.kpis.weighted_amount,
      winRate: d.kpis.win_rate_pct,
      winRatePrev: d.kpis.win_rate_prev,
      conversion: d.kpis.conversion_pct,
      conversionPrev: d.kpis.conversion_prev,
    },
    chart: {
      title: "Revenue won",
      subtitle: "Won deal value per month, last 12 months",
      bars: trend.map((t, i) => ({ label: fmtMonth(t.period), segments: [{ value: t.won_amount, tone: "gold" as const }], current: i === trend.length - 1 })),
    },
    stages: (d.pipeline_by_stage || []).map((s) => ({ id: s.stage_id, name: s.stage_name, deals: s.deals, amount: s.amount })),
    leaders,
    leadersTitle: heads.length ? "Sales Head leaderboard" : "Top executives",
    attention: [
      { key: "unassigned", label: "Unassigned leads in the pool", count: a.unassigned_leads, to: "/leads", tone: "warning" as const },
      { key: "untouched", label: "Leads untouched for 7+ days", count: a.untouched_leads_7d, to: "/leads", tone: "warning" as const },
      { key: "overdue", label: "Overdue tasks", count: a.overdue_tasks, to: "/tasks?view=overdue&scope=team", tone: "danger" as const },
      { key: "escalated", label: "Escalated quote approvals", count: a.escalated_quotes, to: "/quotes", tone: "danger" as const },
    ].filter((r) => r.count > 0),
    approvals: [],
    quotesAwaiting: d.kpis.quotes_awaiting_approval,
  };
}

function fromSalesHead(d: SalesHeadSummary): Omit<ManagerData, "hot" | "agenda" | "forecast" | "funnel"> {
  const f = d.forecast;
  const bucket = (label: string, b: { amount: number; weighted: number }, danger = false, current = false): Bar => ({
    label,
    current,
    segments: [
      { value: b.weighted, tone: danger ? "danger" : "primary" },
      { value: Math.max(0, b.amount - b.weighted), tone: "soft" },
    ],
  });
  const a = d.attention;
  const rows: AttentionRow[] = [
    ...a.stale_deals.slice(0, 3).map((s) => ({ key: `deal:${s.id}`, label: s.name, detail: `${s.owner_name} · ${s.stage_name} · idle ${s.idle_days} days`, to: `/deals/${s.id}`, tone: "warning" as const })),
    ...a.overdue_tasks.slice(0, 2).map((t) => ({ key: `task:${t.id}`, label: t.title, detail: `${t.owner_name} · ${t.related_name || "overdue task"}`, to: "/tasks?view=overdue&scope=team", tone: "danger" as const })),
    ...a.not_contacted_24h.slice(0, 2).map((l) => ({ key: `lead:${l.id}`, label: `${l.name || l.company_name} not contacted in 24h`, detail: l.owner_name, to: `/leads/${l.id}`, tone: "warning" as const })),
  ];
  if (a.unassigned_leads > 0) rows.push({ key: "unassigned", label: "Unassigned leads in the pool", count: a.unassigned_leads, to: "/leads", tone: "info" });
  return {
    role: "SALES_HEAD",
    kpis: {
      revenue: d.kpis.revenue,
      revenuePrev: d.kpis.revenue_prev,
      target: d.kpis.target_amount,
      targetPct: d.kpis.target_achieved_pct,
      pipeline: d.kpis.pipeline_amount,
      weighted: d.kpis.weighted_amount,
      winRate: d.kpis.win_rate_pct,
      winRatePrev: d.kpis.win_rate_prev,
      conversion: d.kpis.conversion_pct,
      conversionPrev: d.kpis.conversion_prev,
    },
    chart: {
      title: "Expected closings",
      subtitle: "Open deals by expected close month: probability-weighted, and the rest of the amount",
      bars: [bucket("Overdue", f.overdue, true), bucket("This month", f.this_month, false, true), bucket("Next month", f.next_month)],
    },
    stages: (d.pipeline_by_stage || []).map((s) => ({ id: s.stage_id, name: s.stage_name, deals: s.deals, amount: s.amount })),
    leaders: (d.team_leaderboard || [])
      .map((r) => ({ id: r.user_id, name: r.name, won: r.won_amount, deals: r.deals_won, target: r.target_amount }))
      .sort((x, y) => y.won - x.won)
      .slice(0, 5),
    leadersTitle: "Team leaderboard",
    attention: rows,
    approvals: d.approval_queue || [],
    quotesAwaiting: null,
  };
}

/** Everything the manager home shows, normalised from the Sales Head and Super Admin summaries */
export function useManagerHome() {
  const { hasPermission } = useAuth();
  const [data, setData] = useState<ManagerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canProposals = hasPermission("proposal.read");
  const canTasks = hasPermission("activity.read");
  const canReports = hasPermission("report.view_own");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const range = `from=${encodeURIComponent(start.toISOString())}&to=${encodeURIComponent(end.toISOString())}`;

    const [summary, hot, agenda, forecast, funnel] = await Promise.allSettled([
      api.get("/api/Dashboard/Summary").then((r) => unwrapObject<(SuperAdminSummary | SalesHeadSummary) & { role: string }>(r)),
      canProposals ? api.get("/api/Proposal/List?status=VIEWED&page_size=6").then((r) => unwrapPage<ProposalListRow>(r).rows) : Promise.resolve([] as ProposalListRow[]),
      canTasks ? api.get(`/api/Task/List?view=today&scope=mine&limit=20&${range}`).then((r) => unwrapPage<Task>(r).rows) : Promise.resolve([] as Task[]),
      canReports ? api.get("/api/Report/QuarterForecast").then((r) => unwrapObject<QuarterForecast>(r)) : Promise.resolve(null),
      canReports ? api.get("/api/Report/Funnel").then((r) => unwrapObject<FunnelData>(r)) : Promise.resolve(null),
    ]);

    if (summary.status === "rejected") {
      setError(summary.reason?.message || "Could not load the overview");
      setLoading(false);
      return;
    }
    const s = summary.value;
    const base = s.role === "SALES_HEAD" ? fromSalesHead(s as SalesHeadSummary) : fromSuperAdmin(s as SuperAdminSummary);
    setData({
      ...base,
      hot: hot.status === "fulfilled" ? hot.value : [],
      agenda: agenda.status === "fulfilled" ? agenda.value : [],
      forecast: forecast.status === "fulfilled" ? forecast.value : null,
      funnel: funnel.status === "fulfilled" ? funnel.value : null,
    });
    setLoading(false);
  }, [canProposals, canTasks, canReports]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}
